import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import api from "../../../const/axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import { cn } from "@/lib/utils";
import {
  Plus, Trash2, CheckCircle2, XCircle, Eye,
  Cloud, CloudOff, Loader2,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Team {
  key: number;
  name: string;
  teamId: number | null;
  answers: (boolean | null)[];
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

// ─── Answer cell ──────────────────────────────────────────────────────────────

const AnswerCell: React.FC<{
  value: boolean | null;
  canEdit: boolean;
  onClick: () => void;
}> = ({ value, canEdit, onClick }) => (
  <button
    type="button"
    onClick={canEdit ? onClick : undefined}
    className={cn(
      "w-8 h-8 flex items-center justify-center mx-auto rounded-full transition-transform duration-100",
      canEdit && "hover:scale-110",
      !canEdit && "cursor-default pointer-events-none"
    )}
  >
    {value === true  && <CheckCircle2 className="w-6 h-6 text-green-500" />}
    {value === false && <XCircle      className="w-6 h-6 text-red-400"   />}
    {value === null  && <div className="w-5 h-5 rounded-full border-2 border-dashed border-gray-200 dark:border-gray-700" />}
  </button>
);

// ─── Save indicator ───────────────────────────────────────────────────────────

const SaveIndicator: React.FC<{ status: SaveStatus }> = ({ status }) => {
  if (status === "idle") return null;
  return (
    <div className={cn(
      "flex items-center gap-1.5 text-xs font-medium transition-all",
      status === "saving" && "text-gray-400 dark:text-gray-500",
      status === "saved"  && "text-green-600",
      status === "error"  && "text-red-500",
    )}>
      {status === "saving" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {status === "saved"  && <Cloud    className="w-3.5 h-3.5" />}
      {status === "error"  && <CloudOff className="w-3.5 h-3.5" />}
      {status === "saving" && "Saxlanılır..."}
      {status === "saved"  && "Saxlanıldı"}
      {status === "error"  && "Xəta"}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ManualGame: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const gameName = (location.state as any)?.gameName || "Yeni Oyun";
  const tournamentId = id ? parseInt(id) : (location.state as any)?.tournamentId;

  const [teams, setTeams]               = useState<Team[]>([]);
  const [questionsCount, setQuestionsCount] = useState(1);
  const [newTeamName, setNewTeamName]   = useState("");
  const [loading, setLoading]           = useState(false);
  const [allTeams, setAllTeams]         = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [canEdit, setCanEdit]           = useState(false);
  const [saveStatus, setSaveStatus]     = useState<SaveStatus>("idle");

  const isFirstSave = useRef(true);

  // ── Fetch all teams for autocomplete ───────────────────────────────────────

  useEffect(() => {
    api.get("teams")
      .then(res => setAllTeams(res.data))
      .catch(err => console.error("Failed to fetch teams", err));
  }, []);

  // ── Load tournament data ───────────────────────────────────────────────────

  useEffect(() => {
    if (!tournamentId) { setCanEdit(true); return; }

    setLoading(true);
    api.get(`tournaments/${tournamentId}/results`)
      .then(res => {
        if (res.data) {
          setCanEdit(res.data.canEdit);
          if (res.data.results) {
            try {
              const data = JSON.parse(res.data.results);
              if (data.teams)          setTeams(data.teams);
              if (data.questionsCount) setQuestionsCount(data.questionsCount);
            } catch (e) {
              console.error("Failed to parse results", e);
            }
          }
        }
      })
      .catch(err => {
        if (err.response?.status !== 404) {
          console.error("Failed to fetch results", err);
          setSaveStatus("error");
        }
      })
      .finally(() => setLoading(false));
  }, [tournamentId]);

  // ── Debounced auto-save ────────────────────────────────────────────────────

  useEffect(() => {
    if (!tournamentId || !canEdit || teams.length === 0) return;
    if (isFirstSave.current) { isFirstSave.current = false; return; }

    setSaveStatus("saving");
    const handler = setTimeout(async () => {
      try {
        await api.put(`tournaments/${tournamentId}/results`, JSON.stringify({
          teams,
          questionsCount,
          updatedAt: new Date().toISOString(),
        }));
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2500);
      } catch {
        setSaveStatus("error");
      }
    }, 3000);

    return () => clearTimeout(handler);
  }, [teams, questionsCount, tournamentId, canEdit]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const addTeam = () => {
    if (!canEdit || !newTeamName.trim()) return;
    const existingTeam = allTeams.find(t => t.name.toLowerCase() === newTeamName.trim().toLowerCase());
    setTeams(prev => [...prev, {
      key: Date.now(),
      name: newTeamName.trim(),
      teamId: existingTeam?.id ?? null,
      answers: Array(questionsCount).fill(null),
    }]);
    setNewTeamName("");
    setShowDropdown(false);
  };

  const addQuestion = () => {
    if (!canEdit) return;
    setQuestionsCount(c => c + 1);
    setTeams(prev => prev.map(t => ({ ...t, answers: [...t.answers, null] })));
  };

  const toggleAnswer = (teamKey: number, qIndex: number) => {
    if (!canEdit) return;
    setTeams(prev => prev.map(team => {
      if (team.key !== teamKey) return team;
      const answers = [...team.answers];
      if (answers[qIndex] === true)       answers[qIndex] = false;
      else if (answers[qIndex] === false) answers[qIndex] = null;
      else                                answers[qIndex] = true;
      return { ...team, answers };
    }));
  };

  const deleteTeam = (key: number) => {
    if (!canEdit) return;
    setTeams(prev => prev.filter(t => t.key !== key));
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const questionRatings = Array.from({ length: questionsCount }, (_, i) => {
    const correct = teams.filter(t => t.answers[i] === true).length;
    return teams.length > 0 ? teams.length - correct : 0;
  });

  const getTeamScore = (team: Team) =>
    team.answers.reduce<number>((acc, ans, i) =>
      ans === true ? acc + questionRatings[i] : acc, 0);

  const getTeamCorrect = (team: Team) =>
    team.answers.filter(a => a === true).length;

  const filteredTeams = allTeams.filter(t =>
    newTeamName && t.name.toUpperCase().includes(newTeamName.toUpperCase())
  );

  // ── Sorted teams for display (by score descending) ─────────────────────────
  const sortedTeams = [...teams].sort((a, b) => getTeamScore(b) - getTeamScore(a));

  // ── Cell / header shared classes ───────────────────────────────────────────
  const thCls = "px-3 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide bg-gray-50/80 border-b border-gray-100 dark:border-gray-800 whitespace-nowrap";
  const tdCls = "px-3 py-2.5 border-b border-gray-50 text-sm";

  // ─ Sticky right offsets (delete=48px, rating=80px, dc=64px) ─────────────
  const W_DEL  = 48;
  const W_RAT  = 80;
  const W_DC   = 64;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>{gameName} | 3sual.az</title>
      </Helmet>

      <div className="max-w-full px-4 sm:px-6 lg:px-8 py-6 space-y-4">

        {/* ── Header card ───────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Title + status */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <h1 className="text-xl font-black text-gray-900 dark:text-gray-50 truncate">{gameName}</h1>
              {!canEdit && (
                <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex-shrink-0">
                  <Eye className="w-3 h-3" />
                  Yalnız izləmək
                </span>
              )}
              <SaveIndicator status={saveStatus} />
            </div>

            {/* Toolbar (edit mode) */}
            {canEdit && (
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Input
                    placeholder="Komanda adı"
                    value={newTeamName}
                    onChange={e => { setNewTeamName(e.target.value); setShowDropdown(true); }}
                    onKeyDown={e => e.key === "Enter" && addTeam()}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    className="w-48"
                  />
                  {showDropdown && filteredTeams.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                      {filteredTeams.map((t: any) => (
                        <button
                          key={t.id}
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          onClick={() => { setNewTeamName(t.name); setShowDropdown(false); }}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button onClick={addTeam} disabled={!newTeamName.trim()} className="gap-1.5" size="sm">
                  <Plus className="w-3.5 h-3.5" />
                  Komanda əlavə et
                </Button>
                <Button onClick={addQuestion} variant="outline" size="sm" className="gap-1.5">
                  <Plus className="w-3.5 h-3.5" />
                  Yeni sual
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* ── Score table ───────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-20 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin dark:text-indigo-300" />
              <p className="text-sm text-gray-400 dark:text-gray-500 font-medium">Yüklənir...</p>
            </div>
          ) : teams.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-gray-400 dark:text-gray-500 font-medium">
                {canEdit ? "Komanda əlavə edin" : "Hələ ki, heç bir komanda yoxdur"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    {/* Team name — sticky left */}
                    <th className={cn(thCls, "sticky left-0 z-20 text-left min-w-[180px]")}>
                      Komanda adı
                    </th>

                    {/* Question columns */}
                    {Array.from({ length: questionsCount }, (_, i) => (
                      <th key={i} className={cn(thCls, "text-center w-[72px]")}>
                        {i + 1}
                      </th>
                    ))}

                    {/* DC — sticky right */}
                    <th
                      className={cn(thCls, "text-center z-20 border-l border-gray-100 dark:border-gray-800")}
                      style={{ position: "sticky", right: W_DEL + W_RAT, width: W_DC }}
                    >
                      DC
                    </th>

                    {/* Rating — sticky right */}
                    <th
                      className={cn(thCls, "text-center z-20")}
                      style={{ position: "sticky", right: W_DEL, width: W_RAT }}
                    >
                      Reytinq
                    </th>

                    {/* Delete — sticky right */}
                    <th
                      className={cn(thCls, "z-20")}
                      style={{ position: "sticky", right: 0, width: W_DEL }}
                    />
                  </tr>
                </thead>

                <tbody>
                  {sortedTeams.map((team, rowIdx) => {
                    const correct = getTeamCorrect(team);
                    const score   = getTeamScore(team);
                    const isTop   = rowIdx === 0 && score > 0;

                    return (
                      <tr
                        key={team.key}
                        className={cn(
                          "transition-colors hover:bg-gray-50/60",
                          isTop && "bg-amber-50/40"
                        )}
                      >
                        {/* Team name — sticky left */}
                        <td className={cn(
                          tdCls,
                          "sticky left-0 z-10 bg-inherit font-semibold text-gray-800 dark:text-gray-100 min-w-[180px]",
                          isTop && "bg-amber-50/40"
                        )}>
                          <div className="flex items-center gap-2">
                            {isTop && (
                              <span className="text-amber-500 text-xs font-bold">🥇</span>
                            )}
                            {!isTop && (
                              <span className="text-xs text-gray-300 font-medium w-4 text-center">
                                {rowIdx + 1}
                              </span>
                            )}
                            {team.name}
                          </div>
                        </td>

                        {/* Answer cells */}
                        {team.answers.map((ans, qi) => (
                          <td key={qi} className={cn(tdCls, "text-center w-[72px]")}>
                            <AnswerCell
                              value={ans}
                              canEdit={canEdit}
                              onClick={() => toggleAnswer(team.key, qi)}
                            />
                          </td>
                        ))}

                        {/* DC — sticky right */}
                        <td
                          className={cn(tdCls, "text-center font-bold z-10 border-l border-gray-100 dark:border-gray-800", isTop ? "bg-amber-50/40" : "bg-white dark:bg-gray-900")}
                          style={{ position: "sticky", right: W_DEL + W_RAT, width: W_DC }}
                        >
                          <span className="inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-indigo-300">
                            {correct}
                          </span>
                        </td>

                        {/* Rating — sticky right */}
                        <td
                          className={cn(tdCls, "text-center font-bold z-10", isTop ? "bg-amber-50/40" : "bg-white dark:bg-gray-900")}
                          style={{ position: "sticky", right: W_DEL, width: W_RAT }}
                        >
                          <span className="inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
                            {score}
                          </span>
                        </td>

                        {/* Delete — sticky right */}
                        <td
                          className={cn(tdCls, "text-center z-10", isTop ? "bg-amber-50/40" : "bg-white dark:bg-gray-900")}
                          style={{ position: "sticky", right: 0, width: W_DEL }}
                        >
                          {canEdit && (
                            <button
                              onClick={() => deleteTeam(team.key)}
                              className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-400 hover:bg-red-50 dark:bg-red-950 transition-colors mx-auto"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                {/* Question difficulty row */}
                <tfoot>
                  <tr className="bg-gray-50/80 border-t-2 border-gray-100 dark:border-gray-800">
                    <td className={cn(
                      "sticky left-0 z-10 bg-gray-50/80 px-3 py-2.5 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide min-w-[180px]"
                    )}>
                      Çətinlik
                    </td>
                    {questionRatings.map((rating, i) => (
                      <td key={i} className="px-3 py-2.5 text-center w-[72px]">
                        <span className={cn(
                          "inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-xs font-semibold",
                          rating >= teams.length * 0.7  && "bg-green-50 dark:bg-green-950 text-green-600",
                          rating >= teams.length * 0.3 && rating < teams.length * 0.7 && "bg-yellow-50 dark:bg-yellow-950 text-yellow-600",
                          rating <  teams.length * 0.3  && "bg-red-50 dark:bg-red-950 text-red-500",
                          teams.length === 0            && "bg-gray-50 dark:bg-gray-950 text-gray-400 dark:text-gray-500",
                        )}>
                          {rating}
                        </span>
                      </td>
                    ))}
                    <td style={{ position: "sticky", right: W_DEL + W_RAT, width: W_DC }} className="bg-gray-50/80 border-l border-gray-100 dark:border-gray-800" />
                    <td style={{ position: "sticky", right: W_DEL, width: W_RAT }}        className="bg-gray-50/80" />
                    <td style={{ position: "sticky", right: 0, width: W_DEL }}            className="bg-gray-50/80" />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* ── Legend ────────────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-4">
          <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Qaydalar</p>
          <ul className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
            <li>• Xanaya klikləyərək cavabı dəyişin: <span className="font-semibold">Boş → Doğru → Yanlış → Boş</span></li>
            <li>• <span className="font-semibold">DC</span> — doğru cavabların sayı</li>
            <li>• <span className="font-semibold">Çətinlik</span> — komanda sayı minus doğru cavab verən komanda sayı</li>
            <li>• <span className="font-semibold">Reytinq</span> — doğru cavablandırılan sualların çətinlik dərəcələri cəmi</li>
          </ul>
        </div>

      </div>
    </div>
  );
};

export default ManualGame;
